"use client";

import { useState } from "react";
import { useToolComputed } from "@/hooks/use-tool-computed";
import { UnitToggle } from "@/components/ui/unit-toggle";
import { CalculatorNumberField } from "@/components/calculators/shared/calculator-number-field";
import {
  CalculatorFields,
  CalculatorSection,
  CalculatorWorkbench,
} from "@/components/calculators/shared/calculator-workbench";
import { ResultMessage } from "@/components/calculators/shared/calculator-results";
import {
  parseNonNegativeNumber,
  parseNumberInRange,
} from "@shared/validation/number-in-range";
import {
  FILL_PERCENT_RANGE,
  convertLength,
  estimateGaylordVolume,
  lengthValue,
  typedLength,
  type GaylordVolumeEstimate,
  type GaylordVolumeInput,
  type LengthEntry,
  type LengthUnit,
} from "./gaylord-volume-model";
import { GaylordVolumeResults } from "./gaylord-volume-results";

const UNIT_OPTIONS: Array<{ value: LengthUnit; label: string; abbrev: string }> = [
  { value: "in", label: "Inches", abbrev: "in" },
  { value: "ft", label: "Feet", abbrev: "ft" },
];

interface GaylordVolumeInputs {
  length: LengthEntry;
  width: LengthEntry;
  height: LengthEntry;
  unit: LengthUnit;
  fillPercent: string;
  density: string;
}

const DEFAULTS: GaylordVolumeInputs = {
  length: typedLength("48"),
  width: typedLength("40"),
  height: typedLength("36"),
  unit: "in",
  fillPercent: "90",
  density: "20",
};

interface GaylordVolumeResult {
  input: GaylordVolumeInput;
  estimate: GaylordVolumeEstimate;
}

function computeResult(inputs: GaylordVolumeInputs): GaylordVolumeResult | null {
  const length = lengthValue(inputs.length, inputs.unit);
  const width = lengthValue(inputs.width, inputs.unit);
  const height = lengthValue(inputs.height, inputs.unit);
  const fillPercent = parseNumberInRange(inputs.fillPercent, FILL_PERCENT_RANGE);
  const densityLbsPerCubicFoot = parseNonNegativeNumber(inputs.density);
  if (length === null || width === null || height === null || fillPercent === null || densityLbsPerCubicFoot === null) {
    return null;
  }
  const input = { length, width, height, unit: inputs.unit, fillPercent, densityLbsPerCubicFoot };
  return { input, estimate: estimateGaylordVolume(input) };
}

interface InputsProps {
  inputs: GaylordVolumeInputs;
  onChange: (next: Partial<GaylordVolumeInputs>) => void;
}

function withUnit(inputs: GaylordVolumeInputs, unit: LengthUnit): Partial<GaylordVolumeInputs> {
  return {
    unit,
    length: convertLength(inputs.length, inputs.unit, unit),
    width: convertLength(inputs.width, inputs.unit, unit),
    height: convertLength(inputs.height, inputs.unit, unit),
  };
}

function DimensionFields({ inputs, onChange }: InputsProps) {
  return (
    <CalculatorSection
      title="Inside dimensions"
      action={
        <UnitToggle
          aria-label="Units"
          value={inputs.unit}
          options={UNIT_OPTIONS}
          onValueChange={(unit) => onChange(withUnit(inputs, unit))}
        />
      }
    >
      <CalculatorFields>
        <CalculatorNumberField id="gaylord-length" label="Length" suffix={inputs.unit} value={inputs.length.text} onChange={(text) => onChange({ length: typedLength(text) })} />
        <CalculatorNumberField id="gaylord-width" label="Width" suffix={inputs.unit} value={inputs.width.text} onChange={(text) => onChange({ width: typedLength(text) })} />
        <CalculatorNumberField id="gaylord-height" label="Height" suffix={inputs.unit} value={inputs.height.text} onChange={(text) => onChange({ height: typedLength(text) })} />
      </CalculatorFields>
    </CalculatorSection>
  );
}

function LoadFields({ inputs, onChange }: InputsProps) {
  return (
    <CalculatorSection title="Load">
      <CalculatorFields>
        <CalculatorNumberField id="gaylord-fill" label="Usable fill" suffix="%" range={FILL_PERCENT_RANGE} value={inputs.fillPercent} onChange={(fillPercent) => onChange({ fillPercent })} />
        <CalculatorNumberField id="gaylord-density" label="Material density" suffix="lb/ft³" value={inputs.density} onChange={(density) => onChange({ density })} />
      </CalculatorFields>
    </CalculatorSection>
  );
}

export function GaylordVolumeCalculator() {
  const [inputs, setInputs] = useState<GaylordVolumeInputs>(DEFAULTS);
  const onChange = (next: Partial<GaylordVolumeInputs>) => setInputs((current) => ({ ...current, ...next }));
  const result = computeResult(inputs);
  useToolComputed("gaylord-volume", result === null ? null : JSON.stringify(inputs));

  return (
    <CalculatorWorkbench
      inputs={
        <>
          <DimensionFields inputs={inputs} onChange={onChange} />
          <LoadFields inputs={inputs} onChange={onChange} />
        </>
      }
      results={
        result === null ? (
          <ResultMessage>Fix the highlighted inputs to see an estimate.</ResultMessage>
        ) : (
          <GaylordVolumeResults input={result.input} estimate={result.estimate} />
        )
      }
    />
  );
}
